import { useState, useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  getSuccessCriteria,
  updateSuccessCriteria,
} from '../service';
import { fetchSubjectsByState } from '../utils/subjects';
import { toolSettingsPatch } from '../utils/toolSettingsPatch';
import { useUserStates } from './useUserStates';

// Helper to convert subject codes to subject objects using subjectsList
const mapCodesToSubjects = (codes, subjectsList) => {
  if (!Array.isArray(codes) || codes.length === 0) return [];
  return codes.map((code) => {
    const codeStr = typeof code === 'object' ? (code.subjectCode || code.code) : code;
    // Try by code first, then by name (codeStr might be a name from legacy data)
    const found = subjectsList.find((s) => (s.subjectCode || s.code) === codeStr)
      || subjectsList.find((s) =>
        String(s.subjectName || s.name || s.title || '').toLowerCase().trim() === String(codeStr).toLowerCase().trim()
      );
    if (found) {
      return { subjectCode: found.subjectCode || found.code, subjectName: found.subjectName || found.name || found.title };
    }
    if (typeof code === 'object' && code.subjectCode && code.subjectName) return code;
    return null;
  }).filter(Boolean);
};

const useSuccessCriteria = (successCriteriaId) => {
  const history = useHistory();
  const queryClient = useQueryClient();
  const { states: userStates } = useUserStates();
  const userState = userStates;
  const [successCriteria, setSuccessCriteria] = useState(null);
  const [subjectsList, setSubjectsList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const sessionRef = useRef(null);
  if (sessionRef.current?.id !== successCriteriaId) {
    sessionRef.current = { id: successCriteriaId, current: null, tail: Promise.resolve(), pending: 0 };
  }
  const session = sessionRef.current;
  const applyCurrent = next => {
    if (sessionRef.current !== session) return;
    session.current = next;
    setSuccessCriteria(next);
  };

  // Fetch subjects for metadata building
  useEffect(() => {
    const loadSubjects = async () => {
      if (userState.length > 0) {
        const subjects = await fetchSubjectsByState(userState);
        setSubjectsList(subjects);
      }
    };
    loadSubjects();
  }, [JSON.stringify(userState)]);

  useEffect(() => {
    let cancelled = false;
    const fetchSuccessCriteria = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const data = await getSuccessCriteria(successCriteriaId);
        if (cancelled) return;

        if (!data || (Array.isArray(data) && data.length === 0)) {
          throw new Error('Success criteria not found');
        }

        // An array response must contain the requested item.
        const criteria = Array.isArray(data)
          ? data.find(item => item.id === successCriteriaId || item._id === successCriteriaId)
          : data;
        if (!criteria) throw new Error('Success criteria not found');

        if (!criteria.criterias || criteria.criterias.length === 0) {
          criteria.criterias = [{ objectives: [] }];
        } else {
          criteria.criterias = criteria.criterias.filter(c => c != null);
          criteria.criterias = criteria.criterias.map(c => ({
            ...c,
            objectives: c.objectives || []
          }));
        }

        const metadata = criteria.metadata || {};
        const years = Array.isArray(metadata.years) ? metadata.years : [];
        const subjects = Array.isArray(metadata.subjects) ? metadata.subjects : [];
        const taskTypes = Array.isArray(metadata.taskTypes) ? metadata.taskTypes : [];
        const responseTypes = Array.isArray(metadata.responseTypes) ? metadata.responseTypes : [];

        applyCurrent({
          ...criteria,
          years,
          subjects,
          taskTypes,
          responseTypes,
          metadata,
        });
      } catch (err) {
        if (cancelled) return;
        console.error('Error fetching success criteria:', err);
        setError(err);
        applyCurrent(null);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    if (successCriteriaId) {
      fetchSuccessCriteria();
    }
    return () => { cancelled = true; };
  }, [successCriteriaId]);

  // Convert subject codes to subject objects when subjectsList becomes available
  useEffect(() => {
    if (successCriteria && subjectsList.length > 0 && successCriteria.subjects?.length > 0) {
      // Check if subjects are strings (codes) that need to be converted
      const firstSubject = successCriteria.subjects[0];
      if (typeof firstSubject === 'string') {
        const mappedSubjects = mapCodesToSubjects(successCriteria.subjects, subjectsList);
        if (session.current) applyCurrent({ ...session.current, subjects: mappedSubjects });
      }
    }
  }, [subjectsList, successCriteria?.subjects?.length]);

  useEffect(() => {
    if (error) {
      history.push('/tools/successcriterias');
    }
  }, [error, history]);

  const applySavedFields = fields => {
    if (session.current) applyCurrent({ ...session.current, ...fields });
  };

  const enqueue = write => {
    session.pending += 1;
    setIsSaving(true);
    const pending = session.tail.catch(() => {}).then(() => {
      if (sessionRef.current !== session) throw new Error('The selected criterion changed.');
      return write();
    });
    session.tail = pending;
    return pending.finally(() => {
      session.pending -= 1;
      if (sessionRef.current === session) setIsSaving(session.pending > 0);
    });
  };

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['successCriteria'] });
    queryClient.invalidateQueries({ queryKey: ['successCriteriaWithPreferences'] });
  };

  const updateSuccessCriteriaTitle = updatedData => {
    if (!session.current) return Promise.resolve();
    return enqueue(async () => {
      const patch = toolSettingsPatch(updatedData, session.current, subjectsList);
      await updateSuccessCriteria(successCriteriaId, patch);
      applySavedFields({ ...updatedData, ...(patch.metadata ? { metadata: patch.metadata } : {}) });
      invalidate();
    });
  };

  const changeObjectives = (criteriaIndex, change, skipApi = false) => {
    if (!session.current) return Promise.resolve();
    const criterias = session.current.criterias.map((criteria, index) =>
      index === criteriaIndex ? { ...criteria, objectives: change(criteria.objectives) } : criteria
    );
    // Apply the local edit synchronously. An older response must never replace
    // a draft added by the click immediately after this objective's blur.
    applySavedFields({ criterias });
    if (skipApi) return Promise.resolve();
    return enqueue(async () => {
      await updateSuccessCriteria(successCriteriaId, { criterias });
      invalidate();
    });
  };

  const addObjective = (criteriaIndex, newObjective, skipApi = false) => {
    const index = session.current?.criterias[criteriaIndex]?.objectives.length;
    return changeObjectives(criteriaIndex, objectives => [...objectives, newObjective], skipApi).then(() => index);
  };

  const updateObjective = (criteriaIndex, objectiveIndex, updatedObjective) =>
    changeObjectives(criteriaIndex, objectives => objectives.map((objective, index) =>
      index === objectiveIndex ? { ...objective, ...updatedObjective } : objective
    ));

  const deleteObjective = (criteriaIndex, objectiveIndex, skipApi = false) =>
    changeObjectives(criteriaIndex, objectives => objectives.filter((_, index) => index !== objectiveIndex), skipApi);

  return {
    successCriteria,
    isLoading,
    isSaving,
    error,
    updateSuccessCriteriaTitle,
    addObjective,
    updateObjective,
    deleteObjective
  };
};

export default useSuccessCriteria;
